import { cn } from '@parcel/ui';
import { Zap } from 'lucide-react';
import * as React from 'react';
import type { Stage } from '../../lib/projects-display.js';
import type { ApiProjectImage, ApiProjectMedia } from '../../lib/projects.js';
import { AuthenticatedImage } from '../auth/authenticated-image.js';

// One fixed cover: the primary image filling the left and the site map on the right, or whichever exists alone.

const SiteMap = React.lazy(() => import('./site-map.js'));

const COVER = {
  compact: { height: 'h-48', mapPx: { width: 480, height: 320 } },
  large: { height: 'h-72', mapPx: { width: 640, height: 400 } },
} as const;

const WIDE_MAP_PX = { width: 1280, height: 400 };

export function ProjectHero({
  media,
  images,
  mapboxToken,
  size,
  theme,
  stage,
  updatesLast30Days = 0,
  onImageError,
  onOpenImage,
}: {
  media: ApiProjectMedia | null | undefined;
  /** Ordered, failed ones removed; the first is the cover. */
  images: ApiProjectImage[];
  mapboxToken: string | undefined;
  size: 'compact' | 'large';
  theme: 'light' | 'dark';
  stage?: Stage | null;
  updatesLast30Days?: number;
  onImageError?: (id: string) => void;
  onOpenImage?: (id: string, opener: HTMLElement) => void;
}) {
  if (!media) return null;
  const image = images[0] ?? null;
  const site = media.site_location && mapboxToken ? media.site_location : null;
  if (!image && !site) return null;
  const cover = COVER[size];

  return (
    <section
      aria-label="Project imagery"
      data-hero-grid
      className={cn(
        'grid overflow-hidden rounded-lg border bg-muted',
        cover.height,
        image && site ? 'grid-cols-[3fr_2fr]' : 'grid-cols-1',
      )}
    >
      {image ? (
        <figure className="relative min-w-0 overflow-hidden">
          <button
            type="button"
            aria-label="View image"
            data-cover-picture
            onClick={(event) => onOpenImage?.(image.id, event.currentTarget)}
            className="block h-full w-full cursor-zoom-in outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <AuthenticatedImage
              key={image.id}
              src={image.url}
              alt={`Project image from ${image.attribution.publisher_name}`}
              className="h-full w-full object-cover"
              onError={() => onImageError?.(image.id)}
            />
          </button>
          <UpdatesChip count={updatesLast30Days} className="left-3 top-3" />
        </figure>
      ) : null}
      {site ? (
        <div className={cn('relative h-full min-w-0', image && 'border-l')}>
          <React.Suspense fallback={<div className="h-full bg-muted" />}>
            <SiteMap
              lat={site.lat}
              lng={site.lng}
              token={mapboxToken}
              theme={theme}
              stage={stage}
              hideCaption
              frameless
              imageSize={image ? cover.mapPx : WIDE_MAP_PX}
            />
          </React.Suspense>
          {image ? null : <UpdatesChip count={updatesLast30Days} className="left-12 top-2" />}
        </div>
      ) : null}
    </section>
  );
}

function UpdatesChip({ count, className }: { count: number; className: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={cn(
        'absolute z-10 inline-flex items-center gap-1 rounded-md bg-background/95 px-2 py-1 text-xs font-medium text-foreground shadow-sm',
        className,
      )}
    >
      <Zap className="h-3.5 w-3.5 text-amber-500" aria-hidden />
      {`${count} ${count === 1 ? 'update' : 'updates'} in last 30 days`}
    </span>
  );
}
